// Wizard step 3 — pass front appearance (slice 4a-3).
//
// Primary + text color (with a contrast warning when they're too close), the
// pass-front detail-field picker, and the stamp icon picker. These are
// COSMETIC fields — editable even after the program is active, so there is no
// readOnly variant.

import { useRef, useState } from 'react'
import { toast } from 'sonner'
import {
  ALL_ONBOARDING_FIELDS,
  HERO_ALLOWED_MIME_TYPES,
  HERO_MAX_BYTES,
  MAX_FRONT_FIELDS,
  NAYA_TERRACOTTA,
  ONBOARDING_FIELD_LABELS,
} from '@naya/shared'
import type { OnboardingFieldKey, PassDesign } from '@naya/shared'

import { OwnerApiError } from '../../ownerApi.js'
import { isMembershipDraft } from '../builderState.js'
import type { BuilderController } from '../hooks/useBuilder.js'
import {
  useDeleteHero,
  useUpdateHeroBrightness,
  useUploadHero,
} from '../hooks/useProgram.js'
import { HeroUploadModal, type HeroUploadResult } from '../HeroUploadModal.js'
import { StampIconPicker } from './StampIconPicker.js'

// A small preset row — Naya terracotta plus common picks.
const COLOR_PRESETS = [
  NAYA_TERRACOTTA,
  '#1F2937', // slate
  '#0E7C66', // teal
  '#7C3AED', // violet
  '#B91C1C', // red
  '#D97706', // amber
]

// Relative-luminance contrast ratio (WCAG). Used only to nudge the owner —
// nothing is blocked on it.
function luminance(hex: string): number {
  const v = hex.replace('#', '')
  const channel = (i: number): number => {
    const c = parseInt(v.slice(i, i + 2), 16) / 255
    return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4
  }
  return 0.2126 * channel(0) + 0.7152 * channel(2) + 0.0722 * channel(4)
}
function contrastRatio(a: string, b: string): number {
  const la = luminance(a)
  const lb = luminance(b)
  return (Math.max(la, lb) + 0.05) / (Math.min(la, lb) + 0.05)
}

function ColorField({
  label,
  value,
  onChange,
}: {
  label: string
  value: string
  onChange: (hex: string) => void
}) {
  return (
    <div className="program-color-field">
      <span className="program-field-label">{label}</span>
      <div className="program-color-controls">
        <input
          type="color"
          className="program-color-swatch"
          value={value}
          onChange={(e) => onChange(e.target.value.toUpperCase())}
          aria-label={`${label} swatch`}
        />
        <input
          type="text"
          className="owner-input program-color-hex"
          value={value}
          maxLength={7}
          onChange={(e) => {
            const v = e.target.value.toUpperCase()
            onChange(v)
          }}
          aria-label={`${label} hex value`}
        />
      </div>
      <div className="program-color-presets">
        {COLOR_PRESETS.map((c) => (
          <button
            key={c}
            type="button"
            className={`program-color-preset${
              value.toUpperCase() === c ? ' is-selected' : ''
            }`}
            style={{ background: c }}
            onClick={() => onChange(c)}
            aria-label={`Use ${c}`}
          />
        ))}
      </div>
    </div>
  )
}

// Lets the owner choose which collected customer fields appear in the
// pass-front detail row. Only ENABLED onboarding fields are offered — first
// name + email are always enabled; the optional ones must be toggled on in
// step 1 first. Selection is capped at MAX_FRONT_FIELDS. This is the same
// control as the inline "Show on pass" toggle in step 1 — both write
// pass_design.front_fields; the owner can use whichever feels natural.
function FrontFieldsPicker({ builder }: { builder: BuilderController }) {
  const { draft } = builder
  const selected = draft.pass_design.front_fields
  // The pickable set: every onboarding field currently enabled, MINUS
  // first_name AND last_name. Both are rendered as the prominent "Member" name
  // ("Juan Dela Cruz"), so showing either again in the front detail row is a
  // true duplicate. email, birthday, phone aren't in that chrome, so they
  // remain pickable.
  const available = ALL_ONBOARDING_FIELDS.filter(
    (key) =>
      key !== 'first_name' &&
      key !== 'last_name' &&
      draft.onboarding_fields[key]?.enabled,
  )
  // Optional fields that the owner has enabled but NOT marked required — a
  // customer can skip them at sign-up, in which case the pass-front detail
  // row will be missing that value. Surface this explicitly.
  const optionalSelected = selected.filter((key) => {
    const f = draft.onboarding_fields[key]
    return f?.enabled && !f.required
  })

  function toggle(key: OnboardingFieldKey) {
    const isSelected = selected.includes(key)
    let next: OnboardingFieldKey[]
    if (isSelected) {
      next = selected.filter((k) => k !== key)
    } else {
      if (selected.length >= MAX_FRONT_FIELDS) return
      next = [...selected, key]
    }
    builder.update({
      pass_design: { ...draft.pass_design, front_fields: next },
    })
  }

  return (
    <div className="program-field-block">
      <span className="program-field-label">Details shown on the pass</span>
      <span className="program-field-hint">
        Pick up to {MAX_FRONT_FIELDS} of the info you collect to show on the
        front of the pass. Turn fields on in “Sign-up info” (step 1) to add
        more here. You can also toggle these inline from step 1.
      </span>
      <div className="program-field-list">
        {available.map((key) => {
          const isSelected = selected.includes(key)
          const atCap = !isSelected && selected.length >= MAX_FRONT_FIELDS
          return (
            <label
              key={key}
              className={`program-field-row${atCap ? ' is-disabled' : ''}`}
            >
              <span className="program-toggle">
                <input
                  type="checkbox"
                  checked={isSelected}
                  disabled={atCap}
                  onChange={() => toggle(key)}
                />
                <span className="program-field-label">
                  {ONBOARDING_FIELD_LABELS[key]}
                </span>
              </span>
            </label>
          )
        })}
      </div>
      {optionalSelected.length > 0 && (
        <p className="program-contrast-warning" role="status">
          Heads up: {optionalSelected
            .map((k) => ONBOARDING_FIELD_LABELS[k])
            .join(', ')}{' '}
          {optionalSelected.length === 1 ? 'is' : 'are'} optional at sign-up.
          If a customer skips {optionalSelected.length === 1 ? 'it' : 'them'},
          the field won't appear on their pass. Mark it required in step 1 to
          guarantee a value.
        </p>
      )}
    </div>
  )
}

// Optional hero photo for the pass background. Two modes:
//   - No hero set: "Upload hero photo" button → file picker → HeroUploadModal.
//   - Hero set: thumbnail + "Adjust" (re-edit brightness) + "Remove" buttons.
// All state lives on pass_design.hero_*; this component just orchestrates
// the file picker, modal, and mutation calls. Re-edit reuses the saved
// hero_original_url as the modal's source (no fresh File).
function HeroSection({ builder }: { builder: BuilderController }) {
  const design = builder.draft.pass_design
  const fileInputRef = useRef<HTMLInputElement | null>(null)
  const [pickedFile, setPickedFile] = useState<File | null>(null)
  const [isReEditing, setIsReEditing] = useState(false)
  const upload = useUploadHero(builder.programId)
  const updateBrightness = useUpdateHeroBrightness(builder.programId)
  const remove = useDeleteHero(builder.programId)

  const hasHero = !!design.hero_original_url
  const modalOpen = pickedFile !== null || isReEditing
  const busy = upload.isPending || updateBrightness.isPending || remove.isPending

  function openPicker() {
    fileInputRef.current?.click()
  }

  function onFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0] ?? null
    e.target.value = ''
    if (!file) return
    if (!HERO_ALLOWED_MIME_TYPES.includes(file.type as never)) {
      toast.error('Hero must be a PNG or JPEG image.')
      return
    }
    if (file.size > HERO_MAX_BYTES) {
      toast.error(
        `Hero must be at most ${Math.round(HERO_MAX_BYTES / (1024 * 1024))}MB.`,
      )
      return
    }
    setPickedFile(file)
  }

  async function handleConfirm(result: HeroUploadResult) {
    try {
      await upload.mutateAsync(result)
      toast.success(hasHero ? 'Hero updated' : 'Hero photo added')
      setPickedFile(null)
      setIsReEditing(false)
    } catch (err) {
      toast.error(
        err instanceof OwnerApiError
          ? err.userMessage
          : 'Could not save the hero photo.',
      )
    }
  }

  async function handleConfirmReEdit(brightness: number) {
    try {
      await updateBrightness.mutateAsync(brightness)
      toast.success('Brightness updated')
      setIsReEditing(false)
    } catch (err) {
      toast.error(
        err instanceof OwnerApiError
          ? err.userMessage
          : 'Could not update brightness.',
      )
    }
  }

  async function handleRemove() {
    try {
      await remove.mutateAsync()
      toast.success('Hero photo removed')
    } catch (err) {
      toast.error(
        err instanceof OwnerApiError
          ? err.userMessage
          : 'Could not remove the hero photo.',
      )
    }
  }

  return (
    <div className="program-field-block">
      <span className="program-field-label">Hero photo (optional)</span>
      <span className="program-field-hint">
        Optional background photo behind your stamps. If you don't upload one,
        the pass shows your primary color as a solid background.
      </span>

      {hasHero ? (
        <div className="program-hero-current">
          <img
            src={design.hero_original_url!}
            alt="Current hero"
            className="program-hero-thumb"
            style={{ filter: `brightness(${1 + design.hero_brightness / 100})` }}
          />
          <div className="program-hero-actions">
            <button
              type="button"
              className="owner-btn"
              onClick={() => setIsReEditing(true)}
              disabled={busy}
            >
              Adjust brightness
            </button>
            <button
              type="button"
              className="owner-btn"
              onClick={openPicker}
              disabled={busy}
            >
              Replace photo
            </button>
            <button
              type="button"
              className="owner-btn program-danger"
              onClick={() => void handleRemove()}
              disabled={busy}
            >
              Remove
            </button>
          </div>
        </div>
      ) : (
        <button
          type="button"
          className="owner-btn"
          onClick={openPicker}
          disabled={busy}
        >
          Upload hero photo
        </button>
      )}

      <input
        ref={fileInputRef}
        type="file"
        accept={HERO_ALLOWED_MIME_TYPES.join(',')}
        style={{ display: 'none' }}
        onChange={onFile}
      />

      {modalOpen && (
        <HeroUploadModal
          file={pickedFile}
          {...(isReEditing && design.hero_original_url
            ? {
                initialOriginalUrl: design.hero_original_url,
                initialBrightness: design.hero_brightness,
              }
            : {})}
          isUploading={upload.isPending || updateBrightness.isPending}
          onConfirm={(r) => void handleConfirm(r)}
          onConfirmReEdit={(b) => void handleConfirmReEdit(b)}
          onCancel={() => {
            if (busy) return
            setPickedFile(null)
            setIsReEditing(false)
          }}
        />
      )}
    </div>
  )
}

export function StepFront({ builder }: { builder: BuilderController }) {
  const design = builder.draft.pass_design
  // SLICE-MEMBERSHIP-PASS — a membership pass has no stamp grid, so the
  // stamp-specific controls (stamp color + stamp icon) are hidden for it.
  const isMembership = isMembershipDraft(builder.draft)

  function setDesign(partial: Partial<PassDesign>) {
    builder.update({ pass_design: { ...design, ...partial } })
  }

  const hexOk = (s: string) => /^#[0-9A-Fa-f]{6}$/.test(s)
  const lowContrast =
    hexOk(design.primary_color) &&
    hexOk(design.text_color) &&
    contrastRatio(design.primary_color, design.text_color) < 3

  return (
    <div className="program-step">
      <h2>How should the pass look?</h2>
      <p className="program-step-sub">
        You can change colors{isMembership ? '' : ' and icons'} any time — even
        after activating.
      </p>

      <div className="program-field-block">
        <label className="program-field-label" htmlFor="program-name">
          Program name
        </label>
        <span className="program-field-hint">
          The big headline on the front of the pass (e.g. "Rewards", "Tea Club",
          "Stamp Card"). Shown right under your business name.
        </span>
        <input
          id="program-name"
          type="text"
          className="owner-input"
          value={builder.draft.name}
          maxLength={60}
          onChange={(e) => builder.update({ name: e.target.value })}
        />
      </div>

      <div className="program-field-block">
        <label
          className="program-field-label"
          htmlFor="program-account-name-label"
        >
          Customer label
        </label>
        <span className="program-field-hint">
          Shown above the customer's name on the pass — e.g. "Member · Juan"
          or "Friend · Maria". Keep it short.
        </span>
        <input
          id="program-account-name-label"
          type="text"
          className="owner-input"
          value={design.account_name_label}
          maxLength={20}
          onChange={(e) => setDesign({ account_name_label: e.target.value })}
        />
      </div>

      <HeroSection builder={builder} />

      <div className="program-field-block">
        <ColorField
          label="Primary color"
          value={design.primary_color}
          onChange={(hex) => setDesign({ primary_color: hex })}
        />
      </div>

      <div className="program-field-block">
        <ColorField
          label="Text color"
          value={design.text_color}
          onChange={(hex) => setDesign({ text_color: hex })}
        />
        {lowContrast && (
          <p className="program-contrast-warning" role="status">
            These two colors are quite close — text may be hard to read on the
            pass.
          </p>
        )}
      </div>

      {!isMembership && (
        <div className="program-field-block">
          <ColorField
            label="Stamp color"
            value={design.stamp_color}
            onChange={(hex) => setDesign({ stamp_color: hex })}
          />
          <span className="program-field-hint">
            Color used for your stamp icons. Pick something that stands out
            against your primary color (or your hero photo, if you've uploaded
            one).
          </span>
        </div>
      )}

      {/* Front detail fields are a loyalty-only concept — a membership pass
          renders only the member name + tier + number + expiry, so the picker
          would be dead config there (its selections never reach the pass). */}
      {!isMembership && <FrontFieldsPicker builder={builder} />}

      {!isMembership && (
        <div className="program-field-block">
          <span className="program-field-label">Stamp icon</span>
          <StampIconPicker
            passDesign={design}
            onChange={(next) => setDesign(next)}
          />
        </div>
      )}
    </div>
  )
}
